import React, { useCallback, useEffect, useRef, useState } from 'react';
import { scrollDrawerIntoViewOnMobile } from 'Src/utils/drawerScroll';
import { useAppDrawer } from './AppDrawerContext';
import {
  focusIntoOverlay,
  getOpenIslandOverlays
} from 'Src/module-federation/scheduling-ui/island-overlays';
import { DEFAULT_TRANSITION_DURATION } from './constants';
import { BaseAppDrawer } from './styles';
/**
 * AppDrawer
 * When used for multiple features this component gives the appearance
 * that one drawer is rendered and only the contents of the drawer is changing.
 * AppDrawerContext provides state and actions for the component.
 */

type TransitionSlotProps = {
  onExited?: (node: HTMLElement) => void;
  [key: string]: unknown;
};

type AppDrawerSlotProps = {
  transition?:
    | TransitionSlotProps
    | ((ownerState: unknown) => TransitionSlotProps);
  [key: string]: unknown;
};

type AppDrawerProps = React.ComponentPropsWithoutRef<'div'> & {
  'aria -labelledby'?: string;
  children: React.ReactNode;
  id: string;
  onClose?: (event: React.SyntheticEvent, ...args: any[]) => void;
  /** Passed to the drawer; on the yield path the transition's onExited is chained */
  slotProps?: AppDrawerSlotProps;
  /**
   * Let overlays from a separate MUI copy (the scheduling-ui React 17 island):
   * modals, menus, poppers and pickers, take focus and Escape while they are
   * open on top of the drawer.
   */
  yieldToForeignModals?: boolean;
};

/** Merge the caller's transition slot props and run both onExited handlers */
const withChainedOnExited = (
  transition: AppDrawerSlotProps['transition'],
  onExited: () => void
): AppDrawerSlotProps['transition'] => {
  const chain = (props: TransitionSlotProps = {}): TransitionSlotProps => ({
    ...props,
    onExited: (node: HTMLElement) => {
      props.onExited?.(node);
      onExited();
    }
  });
  if (typeof transition === 'function') {
    return (ownerState: unknown) => chain(transition(ownerState));
  }
  return chain(transition);
};

const clearBodyScrollLock = () => {
  // Any modal root left (including this drawer, if it re-opened) keeps the lock
  if (!window.document.body.querySelector('.MuiModal-root')) {
    window.document.body.style.removeProperty('overflow');
    window.document.body.style.removeProperty('padding-right');
  }
};

export const AppDrawer: React.FC<AppDrawerProps> = ({
  id,
  children,
  onClose,
  'aria-labelledby': ariaLabelledBy,
  yieldToForeignModals = false,
  slotProps,
  ...props
}) => {
  const { activeAppDrawer, activeAppDrawerBeforeOpenRef } = useAppDrawer();
  const open = activeAppDrawer === id;

  // Modals rendered by a separate MUI copy (the scheduling-ui React 17 island
  // bundles its own @mui/material) have their own ModalManager. Each FocusTrap
  // then thinks it is the top modal: the drawer pulls focus back from the
  // foreign dialog on every move, and Escape closes the drawer instead of the
  // dialog. The foreign ModalManager marks the drawer root aria-hidden while
  // its modal is open, so the drawer yields focus and Escape while it's set.
  const [rootElement, setRootElement] = useState<HTMLDivElement | null>(null);
  const [hiddenByForeignModal, setHiddenByForeignModal] = useState(false);

  useEffect(() => {
    if (!yieldToForeignModals || !open || !rootElement) {
      return undefined;
    }
    const update = () =>
      setHiddenByForeignModal(
        rootElement.getAttribute('aria-hidden') === 'true'
      );
    update();
    const observer = new MutationObserver(update);
    observer.observe(rootElement, {
      attributes: true,
      attributeFilter: ['aria-hidden']
    });
    return () => {
      observer.disconnect();
      // don't start a later open with the yield flags set
      setHiddenByForeignModal(false);
    };
  }, [yieldToForeignModals, open, rootElement]);

  // Not every island overlay goes through a ModalManager: the MUI X date
  // picker is a Popper, and a Menu's autofocus runs while the drawer's trap
  // still enforces focus. So also yield while an island-owned overlay root is
  // open in document.body outside the drawer (see island-overlays.ts for how
  // ownership is told apart from host overlays).
  const [islandOverlay, setIslandOverlay] = useState<Element | null>(null);

  useEffect(() => {
    if (!yieldToForeignModals || !open || !rootElement) {
      return undefined;
    }
    const update = () => {
      const overlays = getOpenIslandOverlays(rootElement);
      setIslandOverlay(overlays[overlays.length - 1] ?? null);
    };
    update();
    const observer = new MutationObserver(update);
    observer.observe(window.document.body, { childList: true });
    return () => {
      observer.disconnect();
      setIslandOverlay(null);
    };
  }, [yieldToForeignModals, open, rootElement]);

  const yieldToForeignModal =
    yieldToForeignModals &&
    open &&
    (hiddenByForeignModal || islandOverlay !== null);

  // Runs after the commit that turned off the drawer's focus enforcement
  // (child effects, including MUI's FocusTrap, run before this one), so the
  // drawer won't pull focus back. Only when focus is still in the drawer: the
  // overlay's own autofocus was pulled back, or it never had one.
  useEffect(() => {
    if (!yieldToForeignModal || !islandOverlay || !rootElement) {
      return;
    }
    const active = window.document.activeElement;
    if (active && rootElement.contains(active)) {
      focusIntoOverlay(islandOverlay);
    }
  }, [yieldToForeignModal, islandOverlay, rootElement]);

  // Each ModalManager also restores body styles from its own record, so a
  // foreign modal can leave `overflow: hidden` behind (captured from the
  // drawer's own scroll lock). Clear it once the drawer is gone, unless another
  // modal still needs the lock.
  const pendingBodyRestoreRef = useRef(false);
  useEffect(() => {
    if (yieldToForeignModals && open) {
      pendingBodyRestoreRef.current = true;
    }
  }, [yieldToForeignModals, open]);

  // The check is deferred after the exit transition (and after an unmount
  // before it): the drawer's children, including the island and its portaled
  // modal root, are still mounted when either runs. The island unmounts after
  // that, and the React 17 ModalManager restores its stale lock in a later
  // task, so wait the exit duration before checking for remaining modals.
  const scheduleBodyRestore = useCallback(() => {
    if (!pendingBodyRestoreRef.current) {
      return;
    }
    pendingBodyRestoreRef.current = false;
    setTimeout(clearBodyScrollLock, DEFAULT_TRANSITION_DURATION.exit);
  }, []);

  useEffect(() => scheduleBodyRestore, [scheduleBodyRestore]);

  const yieldProps = yieldToForeignModals
    ? {
        ref: setRootElement,
        slotProps: {
          ...slotProps,
          transition: withChainedOnExited(
            slotProps?.transition,
            scheduleBodyRestore
          )
        },
        ...(yieldToForeignModal && {
          disableEnforceFocus: true,
          disableEscapeKeyDown: true
        })
      }
    : { slotProps };

  // On mobile, scroll the top of the drawer into view while it's open and
  // restore the previous scroll position once it closes.
  useEffect(() => {
    if (!open) {
      return undefined;
    }
    return scrollDrawerIntoViewOnMobile(id);
  }, [id, open]);

  // If another drawer was already open, give the appearance of a single drawer
  // with changing content by disabling the open/close animation.
  const transitionDuration = activeAppDrawerBeforeOpenRef.current
    ? 0
    : DEFAULT_TRANSITION_DURATION;

  return (
    <BaseAppDrawer
      id={id}
      anchor="right"
      aria-labelledby={ariaLabelledBy}
      onClose={onClose}
      open={open}
      role="dialog"
      transitionDuration={transitionDuration}
      variant="temporary"
      {...props}
      // after props: callers wrapped by withRouter pass `ref: undefined`
      // (React 19 treats ref as a prop), which would drop the root ref
      {...yieldProps}
    >
      {children}
    </BaseAppDrawer>
  );
};
